import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useMemo } from "react";
import { Droplet } from "lucide-react";

import { CurrencyText, TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { Skeleton } from "@/components/common/other/Skeleton";
import { ErrorComponent } from "@/components/common/errors";
import { RaceTrack, Triangle, Dollar, DoubleArrows } from "@/components/common/svgs";
import { cn } from "@/utils/css";
import { getPredictionConfidenceTexts } from "@/utils/confidence";
import { useQuery } from "@/utils/query";
import { getRatePrediction, getRateWithModel } from "@/state/loadRatesSlice";
import { DownloadLoads } from "@/components/dataPages";
import { useIsModelPriceEnabled } from "@/services/featureFlags";

export const RatePrediction = () => {
    const dispatch = useDispatch();
    const { data: predictionData, loading, error } = useSelector((state) => state.loadRates.ratePrediction);
    const { data: modelData } = useSelector((state) => state.loadRates.modelRatePrediction);
    const { data: routeInfo } = useSelector((state) => state.loadRates.marketToMarketRoute);
    const { pickupZip, deliveryZip, trailerType, pickupDate } = useQuery();
    const modelPriceEnabled = useIsModelPriceEnabled();

    useEffect(() => {
        dispatch(getRatePrediction({ trailerType, pickupZip, deliveryZip, pickupDate }));
        if (modelPriceEnabled) {
            dispatch(getRateWithModel({ trailerType, pickupZip, deliveryZip, pickupDate }));
        }
    }, [trailerType, pickupZip, deliveryZip, pickupDate]);

    const confidenceTexts = useMemo(() => {
        return getPredictionConfidenceTexts(predictionData?.confidence);
    }, [predictionData?.confidence]);

    const emptyTrend = predictionData && predictionData.trend > -1 && predictionData.trend < 1;

    return (
        <div className="flex flex-col min-h-[354px]">
            {loading ? (
                <div className="min-h-[354px]">
                    <Skeleton />
                </div>
            ) : error ? (
                <ErrorComponent className="min-h-[354px]" />
            ) : predictionData ? (
                <div className="flex flex-col justify-between min-h-[354px] lg:max-w-[230px]">
                    <div className="flex flex-col">
                        <div className="flex flex-row items-center gap-2">
                            <TranslatedText className="text-base/[25px] text-cm-p-text-primary">Rate prediction</TranslatedText>
                            <InfoButton text="Estimated flat price and RPM for the selected lane." />
                        </div>
                        <div
                            className={cn("mt-1 flex flex-row items-center", {
                                "mb-1": !modelPriceEnabled,
                            })}
                        >
                            <CurrencyText
                                num={predictionData.price.average}
                                round
                                className="text-cm-p-text-primary font-medium text-[24px] leading-[50px]"
                            />
                            <div className="w-[1px] h-4 bg-cm-p-outline mx-3" />
                            <CurrencyText
                                num={predictionData.rate.average}
                                className="text-cm-p-text-primary font-medium text-[24px] leading-[50px]"
                            />
                        </div>
                        {modelPriceEnabled ? (
                            <div className="flex flex-row items-center mb-1">
                                <CurrencyText
                                    num={modelData?.averageLoadPrice.average}
                                    round
                                    className="text-cm-p-text-secondary font-medium text-[16px] leading-[16px]"
                                />
                                <div className="w-[1px] h-4 bg-cm-p-outline mx-3" />
                                <CurrencyText
                                    num={modelData?.averageRate.average}
                                    className="text-cm-p-text-secondary font-medium text-[12px] leading-[25px]"
                                />
                            </div>
                        ) : null}
                        <div
                            className={cn("mt-1 text-sm/[22px] flex flex-row items-center text-cm-p-text-secondary", {
                                "text-cm-p-accent": predictionData.trend > 0,
                                "text-cm-p-on-container-red": predictionData.trend < 0,
                                "text-cm-p-text-secondary": emptyTrend,
                            })}
                        >
                            {emptyTrend ? (
                                <div className="mr-2">
                                    <DoubleArrows />
                                </div>
                            ) : (
                                <div
                                    className={cn("mr-2", {
                                        "rotate-180": predictionData.trend < 0,
                                    })}
                                >
                                    <Triangle />
                                </div>
                            )}
                            <div>
                                {emptyTrend ? (
                                    <TranslatedText>no change </TranslatedText>
                                ) : (
                                    <>
                                        <span className="font-bold">{Math.round(predictionData.trend)}</span>
                                        <span>% </span>
                                    </>
                                )}
                                <TranslatedText>in next days</TranslatedText>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col text-sm/[22px] text-cm-p-text-secondary">
                        <TranslatedText className="text-base/[25px] text-cm-p-text-primary mb-1">Lane stats</TranslatedText>
                        <div className="flex flex-row items-center gap-2 mb-1">
                            <RaceTrack />
                            {routeInfo ? (
                                <div>
                                    {routeInfo.distance} <TranslatedText>miles</TranslatedText>
                                    {/*{routeInfo.durationInSeconds ? `, ${formatDuration(routeInfo.durationInSeconds)}` : ""}*/}
                                </div>
                            ) : null}
                        </div>
                        <div className="flex flex-row items-center gap-2 mb-1 mt-1">
                            <Droplet className="w-3 h-3 text-cm-p-text-secondary" />
                            <div>
                                {predictionData.cost.fuelCost ? (
                                    <>
                                        <CurrencyText
                                            className="text-sm/[22px] text-cm-p-text-secondary font-normal"
                                            num={predictionData.cost.fuelCost}
                                            round
                                        />{" "}
                                        <TranslatedText>fuel</TranslatedText>
                                    </>
                                ) : (
                                    <TranslatedText>No fuel data</TranslatedText>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-row items-center gap-2 mt-1">
                            <Dollar />
                            <div>
                                {predictionData.cost.tollCost ? (
                                    <>
                                        <CurrencyText
                                            className="text-sm/[22px] text-cm-p-text-secondary font-normal"
                                            num={predictionData.cost.tollCost}
                                            round
                                        />{" "}
                                        <TranslatedText>tolls</TranslatedText>
                                    </>
                                ) : (
                                    <TranslatedText>No toll data</TranslatedText>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex flex-row items-center gap-2 text-base text-cm-p-text-primary">
                            <TranslatedText>{confidenceTexts.title}</TranslatedText>{" "}
                            <InfoButton text="The level volume of the load reflects how often it's driven and how easily rates can be predicted." />
                        </div>
                        <div className="text-sm/[22px] text-cm-p-text-secondary">
                            <TranslatedText>{confidenceTexts.description}</TranslatedText>
                        </div>
                    </div>
                    <DownloadLoads filePath={predictionData?.loadsCsvUrl} />
                </div>
            ) : null}
        </div>
    );
};
